import axios, { hasSyncHeader, headersTags } from '@/libs/axios'

import moment from '@/libs/moment'
import expeditionSheets from '@/apis/expedition_sheets'

/**
 @typedef {Object} ExpeditionSheet
 @property {Number} id
 @property {string} uuid
 @property {string} datetime
 @property {Number} plot_id
 @property {Number} crop_id
 @property {Number} crop_type_id
 @property {Number} crop_grade_id
 @property {Number} crop_packing_id
 @property {Number} document_template_id
 @property {Number} quantity
 @property {string} state
 @property {string} send_at
 @property {string} created_at
 @property {string} updated_at
 @property {string} deleted_at
 */

/**
 * @typedef {Object} Family
 * @property {string} slug - Slug de la famille.
 */

/**
 * @typedef {Object} Crop
 * @property {string} id - Identifiant de la culture.
 * @property {string} name - Nom de la culture.
 * @property {string} emoji - Emoji représentant la culture.
 * @property {string} color - Couleur associée à la culture.
 * @property {Family} family - Famille de la culture.
 */

/**
 * @typedef {Object} Type
 * @property {string} id - Identifiant du type.
 * @property {string} name - Nom du type.
 * @property {number[]} weight - Liste des poids individuels.
 * @property {number[]} quantities - Liste des quantités individuelles.
 * @property {number} total_weight - Poids total.
 * @property {number} quantity - Quantité totale.
 */

/**
 * @typedef {Object} Packing
 * @property {string} id - Identifiant du conditionnement.
 * @property {string} name - Nom du conditionnement.
 * @property {number[]} weight - Liste des poids individuels.
 * @property {number[]} quantities - Liste des quantités individuelles.
 * @property {number} total_weight - Poids total des conditionnements.
 * @property {number} quantity - Quantité totale des conditionnements.
 */

/**
 * @typedef {Object} Plot
 * @property {string} id - Identifiant de la parcelle.
 * @property {string} name - Nom de la parcelle.
 * @property {string} abbreviation - Abréviation de la parcelle.
 * @property {number[]} weight - Liste des poids individuels.
 * @property {number[]} quantities - Liste des quantités individuelles.
 * @property {number} total_weight - Poids total de la parcelle.
 * @property {number} quantity - Quantité totale de la parcelle.
 */

/**
 * @typedef {Object} Account
 * @property {string} id - Identifiant du compte.
 * @property {string} name - Nom du compte.
 * @property {number[]} weight - Liste des poids individuels gérés par ce compte.
 * @property {number[]} quantities - Liste des quantités individuelles gérées par ce compte.
 * @property {number} total_weight - Poids total associé au compte.
 * @property {number} quantity - Quantité totale associée au compte.
 */

/**
 * @typedef {Object} Stats
 * @property {Crop} crop - Détails de la culture.
 * @property {Object<string, Type>} types - Liste des types indexés par identifiants.
 * @property {Object<string, Packing>} packings - Liste des conditionnements indexés par identifiants.
 * @property {Object<string, Plot>} plots - Liste des parcelles indexées par identifiants.
 * @property {string[]} options - Liste des options disponibles.
 * @property {Object<string, Account>} accounts - Liste des comptes indexés par identifiants.
 * @property {Array} grades - Liste des grades (actuellement vide).
 * @property {number} total_weight - Poids total global.
 * @property {number} quantity - Quantité totale globale.
 */

/**
 * @typedef {Object} Account
 * @property {number} id - Identifiant du compte.
 * @property {string} name - Nom du compte.
 */

/**
 * @typedef {Object} Address
 * @property {number} id - Identifiant de l'adresse.
 * @property {string} complete_address - Adresse complète.
 * @property {Account} account - Compte associé à l'adresse.
 */

/**
 * @typedef {Object} Farmer
 * @property {string} name - Nom de l'agriculteur.
 */

/**
 * @typedef {Object} Carrier
 * @property {number} id - Identifiant du transporteur.
 * @property {string} name - Nom du transporteur.
 */

/**
 * @typedef {Object} ExpeditionSheet
 * @property {number} id - Identifiant de la feuille d'expédition.
 * @property {string} datetime - Date et heure de l'expédition (format YYYY-MM-DD HH:mm:ss).
 * @property {string} state - État de la feuille d'expédition.
 * @property {string} external_id - Identifiant externe de la feuille d'expédition.
 * @property {string} pdf_url - URL du PDF associé.
 * @property {boolean} is_valid - Indique si la feuille est valide.
 * @property {Farmer} farmer - Agriculteur associé.
 * @property {Account} account - Compte associé à l'expédition.
 * @property {Carrier|null} carrier - Transporteur associé (nullable).
 */

/**
 * @typedef {Object} DeliverySheet
 * @property {string} external_id - Identifiant externe de la livraison.
 * @property {string} json_url - URL du fichier JSON associé.
 * @property {string} xml_url - URL du fichier XML associé.
 * @property {string} pdf_url - URL du fichier PDF associé.
 * @property {Farmer} farmer - Agriculteur associé à cette livraison.
 */

/**
 * @typedef {Object} Delivery
 * @property {string} datetime - Date et heure principales (format YYYY-MM-DD HH:mm:ss).
 * @property {string} state - État principal de l'objet.
 * @property {string} external_id - Identifiant externe principal.
 * @property {boolean} is_valid - Indique si l'objet principal est valide.
 * @property {Farmer[]} farmers - Liste des agriculteurs associés.
 * @property {ExpeditionSheet[]} expedition_sheets - Liste des feuilles d'expédition associées.
 * @property {DeliverySheet[]} delivery_sheets - Liste des feuilles de livraison associées.
 * @property {Account} account - Compte principal associé.
 * @property {Address} account_shipping_address - Adresse de livraison associée au compte.
 * @property {Carrier} carrier - Transporteur principal.
 */

export default {
  namespaced: true,
  state: {
    /** @type {null|Array<ExpeditionSheet>} list */
    list: null,
    /** @type {string|null} lastPull */
    lastPull: null,
  },
  getters: {
    /**
     * @param state
     * @returns {boolean}
     */
    isFetch(state) {
      return state.list !== null
        && state.lastPull !== null
    },
    /**
     * @param state
     * @returns {Array<ExpeditionSheet>}
     */
    getList(state) {
      return state.list
    },
    /**
     * @param state
     * @returns {moment|null}
     */
    getLastPull(state) {
      return state.lastPull ? moment(state.lastPull) : null
    },
    /**
     * @param state
     * @returns {number}
     */
    count(state) {
      return state.list !== null ? state.list.length : 0
    },
    /**
     * @param state
     * @returns {boolean}
     */
    has(state) {
      return state.list !== null && state.list.filter(sheet => sheet.is_valid === true).length >= 1
    },
    /**
     * @param state
     * @returns {boolean}
     */
    hasWithTrashed(state) {
      return state.list !== null && state.list.length >= 1
    },
  },
  mutations: {
    clear(state) {
      state.list = null
      state.lastPull = null
    },
    /**
     * @param state
     * @param {Array<ExpeditionSheet>} list
     *
     * @returns {Promise<void>}
     */
    set(state, list) {
      state.list = list
      state.lastPull = moment()
        .locale('en')
        .format('YYYY-MM-DD HH:mm:ss')
    },
    /**
     * @param state
     * @param {Array<ExpeditionSheet>} completeList
     *
     * @returns {Promise<void>}
     */
    update(state, completeList) {
      state.list = [...completeList, ...state.list]
      state.lastPull = moment()
        .locale('en')
        .format('YYYY-MM-DD HH:mm:ss')
    },
  },
  actions: {
    /**
     * @param commit
     * @param getters
     * @param state
     * @param rootGetters
     * @returns {Promise<boolean>}
     */
    async fetch({
      commit,
      getters,
      state,
      rootGetters,
    }) {
      const fields = [
        'farmer.id',
        'farmer.name',
        'external_id',
        'crop_family.id',
        'crop_family.slug',
        'crop.slug',
        'crop.name',
        'crop.emoji',
        'crop.color',
        'crop_type.slug',
        'crop_type.name',
        'crop_type.options',
        'crop_grade.slug',
        'crop_grade.name',
        'crop_grade.weight',
        'crop_packing.slug',
        'crop_packing.name',
        'crop_packing.weight',
        'account.id',
        'account.name',
        'quantity',
        'datetime',
        'plot.slug',
        'plot.name',
        'plot.abbreviation',
        'carrier.id',
        'carrier.name',
        'quantity',
        'state',
        'send_at',
        'is_valid',
        'pdf_url',
        'xml_url',
        'json_url',
      ]

      const pullAll = async () => {
        /** @type {AxiosResponse<loginResponse>} response */
        const response = await (axios(await (expeditionSheets.list(
          rootGetters['auth/getToken'],
          rootGetters['farmers/getSelectedFarmers'].map(f => f.id),
          moment()
            .locale('en')
            .format('YYYY-MM-DD'),
          {},
          {
            [headersTags.NO_PAGINATE]: true,
            [headersTags.FIELDS]: JSON.stringify(fields),
          },
        ))))

        commit('clear')
        commit('set', response.data.data)
      }

      const sync = async () => {
        const response = await (axios(await (expeditionSheets.list(
          rootGetters['auth/getToken'],
          rootGetters['farmers/getSelectedFarmers'].map(f => f.id),
          moment()
            .locale('en')
            .format('YYYY-MM-DD'),
          {},
          {
            [headersTags.ORDER_BY]: JSON.stringify({
              datetime: 'desc',
            }),
            [headersTags.NO_PAGINATE]: true,
            [headersTags.FIELDS]: JSON.stringify(fields),
            [headersTags.SYNC_DATE]: moment(state.lastPull)
              .locale('en')
              .format('YYYY-MM-DD'),
            [headersTags.SYNC_IDS]: Object.values(state.list)
              .map(data => data.id),
          },
        ))))

        if (!hasSyncHeader(response?.headers)) {
          commit('set', response.data.data)
        } else {
          commit('update', response.data.data)
        }
      }

      if (getters.isFetch) {
        await sync()
      } else {
        await pullAll()
      }

      return true
    },
  },
}
